import { useRef, useState, type ReactNode } from 'react';

export interface SwipeAction {
  /** Stable key. */
  key: string;
  label: string;
  /** Material Symbols icon name. */
  icon?: string;
  onClick: () => void;
  /** Background color intent. `danger` = red, `default` = ochre, `neutral` = gray. */
  variant?: 'danger' | 'default' | 'neutral';
}

interface SwipeableRowProps {
  children: ReactNode;
  /** Actions revealed by swiping left (shown on the right edge). */
  actions: SwipeAction[];
  /** Disable the swipe gesture. */
  disabled?: boolean;
  className?: string;
}

const ACTION_WIDTH = 76; // px per action button

const VARIANT_BG: Record<NonNullable<SwipeAction['variant']>, string> = {
  danger: 'bg-red-500 text-white',
  default: 'bg-primary text-white',
  neutral: 'bg-gray-500 text-white',
};

/**
 * iOS-style swipe-to-reveal list row. Swipe left (touch) to expose action
 * buttons with snap + threshold. On desktop (no touch) the same actions appear
 * on hover as an overlaid kebab-free button strip, so functionality is never lost.
 */
export function SwipeableRow({ children, actions, disabled = false, className = '' }: SwipeableRowProps) {
  const [offset, setOffset] = useState(0); // negative = revealed
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const startOffset = useRef(0);
  const openWidth = actions.length * ACTION_WIDTH;

  const onTouchStart = (e: React.TouchEvent) => {
    if (disabled || actions.length === 0) return;
    startX.current = e.touches[0].clientX;
    startOffset.current = offset;
    setDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragging) return;
    const delta = e.touches[0].clientX - startX.current;
    let next = startOffset.current + delta;
    next = Math.min(0, Math.max(-openWidth - 24, next)); // clamp with a little rubber-band
    setOffset(next);
  };

  const onTouchEnd = () => {
    if (!dragging) return;
    setDragging(false);
    // Snap open if dragged past half the reveal width, else closed.
    setOffset(offset < -openWidth / 2 ? -openWidth : 0);
  };

  const close = () => setOffset(0);

  const runAction = (a: SwipeAction) => {
    close();
    a.onClick();
  };

  return (
    <div className={`group relative overflow-hidden ${className}`}>
      {/* Action strip — sits behind the row on mobile, fades in on hover for desktop. */}
      <div
        className="absolute inset-y-0 right-0 flex"
        style={{ width: openWidth }}
        aria-hidden={offset === 0}
      >
        {actions.map(a => (
          <button
            key={a.key}
            type="button"
            onClick={() => runAction(a)}
            tabIndex={offset === 0 ? -1 : 0}
            style={{ width: ACTION_WIDTH }}
            className={`flex h-full flex-col items-center justify-center gap-0.5 text-xs font-semibold active:brightness-90 ${
              VARIANT_BG[a.variant ?? 'default']
            }`}
          >
            {a.icon && <span className="material-symbols-outlined text-[20px]">{a.icon}</span>}
            {a.label}
          </button>
        ))}
      </div>

      {/* Foreground content. */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        className="relative bg-white"
        style={{
          transform: `translateX(${offset}px)`,
          transition: dragging ? 'none' : 'transform 0.28s cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {children}
      </div>

      {/* Desktop hover affordance — quick action buttons (no touch needed). */}
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden items-center gap-1 pr-2 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 md:flex">
        {actions.map(a => (
          <button
            key={a.key}
            type="button"
            onClick={() => runAction(a)}
            title={a.label}
            aria-label={a.label}
            className={`flex h-8 w-8 items-center justify-center rounded-lg shadow-sm active:scale-95 ${
              VARIANT_BG[a.variant ?? 'default']
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">{a.icon ?? 'more_horiz'}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
